import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

// eslint-disable-next-line react-refresh/only-export-components
export const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const [mode, setMode] = useState('lecture'); // 'lecture' or 'edition'
  const [deviceMode, setDeviceMode] = useState('desktop'); // 'desktop', 'tablet', 'mobile'
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('rectorium_theme') || 'light';
  });

  const [showStudentModal, setShowStudentModal] = useState(false);
  const [editStudentId, setEditStudentId] = useState(null);
  const [showUEModal, setShowUEModal] = useState(false);
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [showTeacherModal, setShowTeacherModal] = useState(false);
  const [editTeacherId, setEditTeacherId] = useState(null);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [editStaffId, setEditStaffId] = useState(null);
  const [showAiImporter, setShowAiImporter] = useState(false);
  const [aiImporterEntity, setAiImporterEntity] = useState('subjects');
  // Assistant guidé de configuration — invocable depuis n'importe où (fil conducteur).
  const [showAssistant, setShowAssistant] = useState(false);
  const [editSubject, setEditSubject] = useState(null);

  const [editUnit, setEditUnit] = useState(null);
  const [addToUE, setAddToUE] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const openAiImporter = useCallback((entity = 'subjects') => {
    setAiImporterEntity(entity);
    setShowAiImporter(true);
  }, []);

  const triggerRefresh = useCallback(() => setRefreshKey(prev => prev + 1), []);

  useEffect(() => {
    localStorage.setItem('rectorium_theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // DWA (Dual Workspace Architecture) : le mode de travail est un état global
  // stylable — Mode Vue (pilotage) vs Mode Édition (configuration, accent ambre).
  useEffect(() => {
    document.documentElement.setAttribute('data-workspace-mode', mode);
  }, [mode]);

  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  }, []);

  const value = useMemo(() => ({
    mode, setMode,
    deviceMode, setDeviceMode,
    theme, toggleTheme,
    sidebarOpen, setSidebarOpen,
    showStudentModal, setShowStudentModal,
    editStudentId, setEditStudentId,
    showUEModal, setShowUEModal,
    showUnitModal, setShowUnitModal,
    showTeacherModal, setShowTeacherModal,
    editTeacherId, setEditTeacherId,
    showStaffModal, setShowStaffModal,
    editStaffId, setEditStaffId,
    showAiImporter, setShowAiImporter,
    aiImporterEntity, openAiImporter,
    showAssistant, setShowAssistant,
    editSubject, setEditSubject,
    editUnit, setEditUnit,
    addToUE, setAddToUE,
    refreshKey, triggerRefresh
  }), [
    mode, deviceMode, theme, toggleTheme, sidebarOpen,
    showStudentModal, editStudentId,
    showUEModal, showUnitModal,
    showTeacherModal, editTeacherId,
    showStaffModal, editStaffId,
    showAiImporter, aiImporterEntity, openAiImporter,
    showAssistant, editSubject, editUnit, addToUE,
    refreshKey, triggerRefresh
  ]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

